import {sendAnalyticsEvent} from '#/analytics/analytics';
import {SuccessIndicator} from '#/atoms/dots-loading-indicator';
import {Modal} from '#/atoms/modal';
import {TokenTransferStatus} from '#/components/testnet-account-funding/components/token-transfer-status';
import {testnetAccountFundingRequests} from '#/components/testnet-account-funding/repo/fetch-testnet-account-funding';
import {ModalContext, ModalContextValue} from '#/contexts/modal-provider';
import {WalletContext} from '#/contexts/wallet-provider';
import {useInvalidateQueries} from '#/hooks/query/base/use-invalidate-queries';
import {useNativeTokenBalance} from '#/hooks/query/use-native-token-balance';
import {useOnCountdownEnd} from '#/hooks/use-countdown';
import useERC20Contract from '#/hooks/use-erc20-contract';
import {wagmiConfig} from '#/wagmi/wagmi-config';
import {useQuery} from '@tanstack/react-query';
import {FunctionComponent, createContext, useContext, useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {testnetAccountFundingTargetAmounts} from 'shareland-common';
import {waitForTransactionReceipt} from 'wagmi/actions';

type Step = 'amount-selection' | 'transfer' | 'confirmation';

type TContext = {
  step: Step;
  setStep: React.Dispatch<React.SetStateAction<Step>>;
  closeModal: ModalContextValue['close'];
};

const initialValue: TContext = {} as TContext;
const Context = createContext<TContext>(initialValue);

export const NewAccountFundedModal: FunctionComponent = () => {
  const {close: closeModal} = useContext(ModalContext);

  const {t} = useTranslation('translation');

  const [step, setStep] = useState<Step>('amount-selection');

  return (
    <Modal theme='dark' title={t('pages.newAccountFunded.title')} onClose={closeModal} hideOnClickAway={false}>
      <Context.Provider value={{step, setStep, closeModal}}>
        <div className='py-4 px-6 text-white space-y-4 w-full md:w-[340px]'>
          <Sections />
        </div>
      </Context.Provider>
    </Modal>
  );
};

const Sections: FunctionComponent = () => {
  const {step} = useContext(Context);

  switch (step) {
    case 'amount-selection':
      return <AmountSelectionSection />;
    case 'transfer':
      return <TransferSection />;
    case 'confirmation':
      return <ConfirmationSection />;
  }
};

const AmountSelectionSection: FunctionComponent = () => {
  const {
    accountResult: {address: account, chain},
    walletClient,
    web3AuthSignatureResult: {data: web3AuthIdTokenGroup},
  } = useContext(WalletContext);
  const {setStep} = useContext(Context);

  const {t} = useTranslation('translation');

  const onStartTransferClickEnabled = !!account && !!web3AuthIdTokenGroup && !!walletClient;
  const onStartTransferClick = async () => {
    if (!onStartTransferClickEnabled) return;

    sendAnalyticsEvent.fundingStarted({});

    setStep('transfer');
  };

  return (
    <>
      <div className='text-sm font-normal'>
        <Trans i18nKey='pages.newAccountFunded.sections.amountSelection.explanation' />
      </div>

      <div className='space-y-2'>
        <button
          className={
            'w-full px-4 py-2 text-white rounded flex-center bg-deepblue hover:opacity-70 disabled:grayscale disabled:opacity-50 disabled:pointer-events-none'
          }
          onClick={onStartTransferClick}
          disabled={!onStartTransferClickEnabled}
        >
          {t('actions.transfer')}
        </button>

        <div className='text-gray-400 text-xs font-semibold'>
          {t('pages.newAccountFunded.sections.amountSelection.testTokenTransferDisclaimer', {
            nativeTokenSymbol: chain?.nativeCurrency.symbol ?? '',
          })}
        </div>
      </div>
    </>
  );
};

const TransferSection: FunctionComponent = () => {
  const {t} = useTranslation('translation');

  const {setStep} = useContext(Context);

  const {
    accountResult: {address: account, chain},
  } = useContext(WalletContext);

  const erc20Contract = useERC20Contract();

  const {data: nativeTokenBalance} = useNativeTokenBalance();
  const [nativeBalanceBelowTreshold] = useState(nativeTokenBalance < testnetAccountFundingTargetAmounts.minNative);
  const nativeTokenTransferQueryEnabled = !!account && nativeBalanceBelowTreshold;
  const nativeTokenTransferQuery = useQuery({
    queryKey: ['native-token-transfer', account],
    queryFn: async () => {
      const res = await testnetAccountFundingRequests.native({account: account!});
      return res;
    },
    retry: 0,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: nativeTokenTransferQueryEnabled,
  });
  const nativeTransferCompleted = nativeTokenTransferQuery.status === 'success' || !nativeBalanceBelowTreshold;

  const stableTokenTransferQueryEnabled = !!account && !!erc20Contract && nativeTransferCompleted;
  const stableTokenTransferQuery = useQuery({
    queryKey: ['stable-token-transfer', account],
    queryFn: async () => {
      const hash = await erc20Contract!.fundAccount();

      const transactionReceipt = await waitForTransactionReceipt(wagmiConfig, {
        hash,
      });

      if (!transactionReceipt) {
        throw new Error();
      }
      return true;
    },
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: stableTokenTransferQueryEnabled,
  });

  const allTransfersCompleted = nativeTransferCompleted && stableTokenTransferQuery.status === 'success';

  useEffect(() => {
    if (allTransfersCompleted) {
      sendAnalyticsEvent.fundingCompleted({});
      setStep('confirmation');
    }
  }, [allTransfersCompleted, setStep]);

  return (
    <>
      <div className='text-sm font-normal'>{t('pages.newAccountFunded.sections.transfer.explanation')}</div>

      <TokenTransferStatus
        title={t('pages.newAccountFunded.sections.transfer.nativeToken.title', {
          nativeTokenSymbol: chain?.nativeCurrency.symbol ?? '',
        })}
        queryEnabled={nativeTokenTransferQueryEnabled}
        query={nativeTokenTransferQuery}
        bypass={!nativeBalanceBelowTreshold}
      />

      <TokenTransferStatus
        title={t('pages.newAccountFunded.sections.transfer.stableToken.title')}
        queryEnabled={stableTokenTransferQueryEnabled}
        query={stableTokenTransferQuery}
        idleMessage={t('pages.newAccountFunded.sections.transfer.stableToken.idleMessage')}
      />
    </>
  );
};

const ConfirmationSection: FunctionComponent = () => {
  const invalidateQueries = useInvalidateQueries();

  const {closeModal} = useContext(Context);

  const {t} = useTranslation('translation');

  const onStartTrading = () => {
    closeModal();
  };

  const countdownToClose = useOnCountdownEnd(onStartTrading, 4);

  useEffect(() => {
    invalidateQueries.useNativeTokenBalance();
    invalidateQueries.useStableTokenBalance();

    return () => {
      invalidateQueries.useFundingStatus();
    };
  }, [invalidateQueries]);

  return (
    <>
      <div className='grow flex-center text-center'>
        <SuccessIndicator widthClassName='w-[100px]'>
          {t('pages.newAccountFunded.sections.confirmation.transferSuccessful')}
        </SuccessIndicator>
      </div>

      <button
        className={
          'w-full px-4 py-2 text-white rounded flex-center bg-deepblue hover:opacity-70 disabled:grayscale disabled:opacity-50 disabled:pointer-events-none'
        }
        onClick={onStartTrading}
      >
        {t('pages.newAccountFunded.sections.confirmation.startTradingCta', {countdownToClose})}
      </button>
    </>
  );
};
